import {
  buildImageValidationMessage,
  validateImageFile,
} from '../../composables/useImageValidation';

const firstPresent = values => values.find(value => value !== undefined);

export const MEDIA_TYPES = {
  PHOTOS: 'fotos',
  VIDEO: 'video',
  VIDEO_360: 'video360',
  BLUEPRINT: 'planta',
};

export const MEDIA_TAB_LABELS = {
  [MEDIA_TYPES.PHOTOS]: 'Fotos',
  [MEDIA_TYPES.VIDEO]: 'Vídeos',
  [MEDIA_TYPES.VIDEO_360]: 'Tour 360°',
  [MEDIA_TYPES.BLUEPRINT]: 'Planta',
};

export const MEDIA_TYPE_ORDER = [
  MEDIA_TYPES.PHOTOS,
  MEDIA_TYPES.VIDEO,
  MEDIA_TYPES.VIDEO_360,
  MEDIA_TYPES.BLUEPRINT,
];

const normalizeCover = items =>
  items.map((item, index) => ({
    ...item,
    position: index + 1,
    isCover: index === 0,
  }));

export const mediaTypeOf = item => {
  const media = typeof item === 'string' ? { src: item } : item || {};
  const descriptor = [
    media.mediaType,
    media.type,
    media.kind,
    media.fileType,
    media.category,
    media.mimeType,
    media.contentType,
    media.url,
    media.src,
    media.embedUrl,
    media.videoUrl,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (/(blueprint|floorplan|planta|plantas|planos?)/.test(descriptor)) {
    return MEDIA_TYPES.BLUEPRINT;
  }

  if (/(video360|360|tour)/.test(descriptor)) {
    return MEDIA_TYPES.VIDEO_360;
  }

  if (
    /(youtube|youtu\.be|vimeo|video|\.mp4|\.mov|\.webm|\.m3u8)/.test(descriptor)
  ) {
    return MEDIA_TYPES.VIDEO;
  }

  return MEDIA_TYPES.PHOTOS;
};

export const isVideoMediaType = type =>
  [MEDIA_TYPES.VIDEO, MEDIA_TYPES.VIDEO_360].includes(type);

export const normalizeMediaItems = (items = []) => {
  const normalized = items.map((item, index) => {
    const media = typeof item === 'string' ? { id: item, src: item } : item;
    const type = mediaTypeOf(media);

    return {
      id: firstPresent([
        media.id,
        media.uuid,
        media.key,
        media.url,
        media.src,
        index,
      ]),
      src: firstPresent([
        media.src,
        media.url,
        media.embedUrl,
        media.videoUrl,
        media.iframeUrl,
        media.previewUrl,
        media.thumbUrl,
        '',
      ]),
      thumbSrc: firstPresent([
        media.thumbSrc,
        media.thumbUrl,
        media.thumbnail,
        media.thumbnailUrl,
        media.previewUrl,
        media.src,
        media.url,
        '',
      ]),
      alt: firstPresent([media.alt, media.title, `Foto ${index + 1}`]),
      type,
      originalType: firstPresent([media.type, media.mediaType, 'image']),
      status: firstPresent([media.status, 'ready']),
      error: firstPresent([media.error, '']),
      width: media.width,
      height: media.height,
      size: firstPresent([media.size, media.file?.size]),
      file: media.file,
      raw: item,
      isCover: Boolean(media.isCover),
    };
  });

  const coverIndex = normalized.findIndex(item => item.isCover);
  if (coverIndex > 0) {
    const [cover] = normalized.splice(coverIndex, 1);
    normalized.unshift(cover);
  }

  return normalizeCover(normalized);
};

export const groupMediaByType = (items = []) =>
  normalizeMediaItems(items).reduce(
    (groups, item) => {
      const type = mediaTypeOf(item);
      groups[type] = [...(groups[type] || []), { ...item, type }];
      return groups;
    },
    {
      [MEDIA_TYPES.PHOTOS]: [],
      [MEDIA_TYPES.VIDEO]: [],
      [MEDIA_TYPES.VIDEO_360]: [],
      [MEDIA_TYPES.BLUEPRINT]: [],
    }
  );

export const buildMediaTabs = (groups = {}) =>
  MEDIA_TYPE_ORDER.map(id => ({
    id,
    label: MEDIA_TAB_LABELS[id],
    count: groups[id]?.length || 0,
  })).filter(tab => tab.count > 0);

export const moveMediaItem = (items, fromIndex, toIndex) => {
  const list = normalizeMediaItems(items);
  const from = Number(fromIndex);
  const to = Number(toIndex);

  if (
    Number.isNaN(from) ||
    Number.isNaN(to) ||
    from < 0 ||
    to < 0 ||
    from >= list.length ||
    to >= list.length ||
    from === to
  ) {
    return list;
  }

  const [item] = list.splice(from, 1);
  list.splice(to, 0, item);
  return normalizeCover(list);
};

export const setCoverMediaItem = (items, id) => {
  const list = normalizeMediaItems(items);
  const index = list.findIndex(item => item.id === id);
  if (index <= 0) return list;

  return moveMediaItem(list, index, 0);
};

export const toggleSelectedMediaId = (selectedIds, id, maxSelectable) => {
  const ids = Array.from(selectedIds || []);
  if (ids.includes(id)) return ids.filter(selectedId => selectedId !== id);
  if (maxSelectable && ids.length >= maxSelectable) return ids;

  return [...ids, id];
};

export { validateImageFile };
export const buildMediaValidationMessage = buildImageValidationMessage;
